// §2.20 WHAT THE CUSTOMER WAS TOLD (Module 09, P3-E PR-E1, D-291) — ONE
// table for §4.7's notifications panel on the order detail and §4.4's
// Notifications card on the quote composer, so the two surfaces cannot word
// the same row two ways (spec 09 §4.4's PR-E1 annotation: "the renderings
// §4.7's panel uses").
//
// A COMPONENT, NOT A SCREEN EXPORT. Both screens import it from here. It used
// to live in screens/order-detail.tsx, and the composer's import of it pulled
// the whole order-detail module into the composer's chunk: one screen
// importing another is the dependency this file exists to break.
//
// Since the dispatcher, a row is written `queued` by the transition itself
// and sent seconds later — or HELD for the tenant's quiet hours, or skipped
// (D-291 rulings 1, 3, 4). `skipped_superseded` has three sources, and the
// row's sentence is written to be true of each: at a quiet-hours release the
// newer status push of an order goes and the older ones do not; a push whose
// order had moved on before it could be sent is not sent (the premise check);
// and a request acknowledgment that the quote itself overtook — the quote was
// sent, or went further, first — is not sent either. So the table reads
// every state the §2.20 CHECK admits, each in words: the kind, the status and
// the failure reason are codes on the wire and never reach the glass as codes
// (each row carries them as `data-kind` / `data-status` / `data-reason` for
// the walkthroughs and for support). The read refreshes itself while a row can
// still move (`notificationRefetchInterval`, hooks/quote-queries.ts).
//
// INSTANTS ARE THE TENANT'S. "Held until 08:00" is a statement about the
// tenant's quiet hours (`default_timezone`, D-291 ruling 3), so it is
// formatted in that zone and in no other: while the zone is not known yet the
// sentence drops the time rather than print the reader's own clock, which
// for anyone outside the plant's zone would name the wrong hour. The When
// column follows the same zone (formatTimestamp's D-089 fallback applies to
// it, as everywhere else in the office).

import type { ReactNode } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  DataTable,
  type DataTableClientPager,
  type DataTableColumn,
  type DataTablePager,
} from "../../../../components/data-table";
import { StatusChip, type ChipVariant } from "../../../../components/status-chip";
import { facilityTodayIso } from "../../../../lib/business-dates";
import { formatTimestamp } from "../../../../lib/datetime";
import { useTenantTimezone } from "../../../../lib/role-codes";
import type { CustomerNotification, CustomerNotificationStatus } from "../hooks/quote-queries";

/**
 * The chip a §2.20 status wears — the product-wide law (green good, amber
 * attention, red failed). `skipped_no_channel` is amber: nobody told the
 * customer, and someone should find out why. An opt-out, a superseded row and
 * a row whose customer was archived (D-317) are neutral: nothing went out ON
 * PURPOSE. Exhaustive over the contract's nine-value enum, so a tenth status
 * fails the build instead of shipping a silent chip.
 */
export function notificationChipVariant(status: CustomerNotificationStatus): ChipVariant {
  switch (status) {
    case "sent":
    case "delivered":
      return "ok";
    case "failed":
      return "bad";
    case "skipped_no_channel":
      return "warn";
    case "queued":
    case "held":
    case "skipped_opted_out":
    case "skipped_superseded":
    case "skipped_customer_archived":
      return "neutral";
  }
}

/**
 * The failure codes this screen has a sentence for: the dispatcher's two
 * (`TEMPLATE_NOT_FOUND`, and `SEND_ERROR` after its fifth attempt — D-291
 * ruling 6), the delivery rollup's one (`DELIVERY_FAILED`, ruling 10), and
 * `TEMPLATE_VARS_INVALID` — module 17's refusal of a template's variables,
 * which the pre-dispatcher inline push recorded as its reason on rows that
 * are still on file. Any other code reads the general sentence.
 */
export const NOTIFICATION_REASONS: ReadonlySet<string> = new Set([
  "TEMPLATE_NOT_FOUND",
  "TEMPLATE_VARS_INVALID",
  "SEND_ERROR",
  "DELIVERY_FAILED",
]);

/** The i18n key of a failed row's reason sentence. */
export function notificationReasonKey(reason: string | null): string {
  return reason !== null && NOTIFICATION_REASONS.has(reason)
    ? `sales.notification.reason.${reason}`
    : "sales.notification.reason.other";
}

/**
 * An instant in the TENANT's clock, for a sentence about the tenant's own
 * hours: the time alone when it falls on the tenant's today, the date and the
 * time otherwise. `undefined` — never the reader's clock — while the zone is
 * unknown or the runtime refuses its name.
 */
export function formatTenantInstant(
  iso: string,
  timeZone: string | undefined,
  nowMs: number,
  locale: string,
): string | undefined {
  if (timeZone === undefined || timeZone.length === 0) return undefined;
  const stamp = Date.parse(iso);
  if (Number.isNaN(stamp)) return undefined;
  try {
    const sameDay = facilityTodayIso(stamp, timeZone) === facilityTodayIso(nowMs, timeZone);
    return new Intl.DateTimeFormat(
      locale,
      sameDay
        ? { timeStyle: "short", timeZone }
        : { dateStyle: "medium", timeStyle: "short", timeZone },
    ).format(stamp);
  } catch {
    return undefined;
  }
}

/** The sentence under a row's chip, where its status needs one. */
export type NotificationDetail =
  | { readonly kind: "held" | "retry" | "superseded" | "no_channel"; readonly text: string }
  | { readonly kind: "failed"; readonly text: string; readonly reason: string };

export function notificationDetail(
  t: TFunction,
  row: Pick<CustomerNotification, "status" | "send_after" | "failed_reason">,
  timeZone: string | undefined,
  nowMs: number,
  locale: string,
): NotificationDetail | undefined {
  switch (row.status) {
    case "held": {
      // `send_after` is the release instant computed when the row was held;
      // the CURRENT settings decide the actual release (ruling 3), so a
      // shortened window can send it earlier than this says. The contract
      // requires it on a held row (0066's CHECK); a row without one still
      // reads as held, with no time.
      const when =
        row.send_after === null
          ? undefined
          : formatTenantInstant(row.send_after, timeZone, nowMs, locale);
      return {
        kind: "held",
        text:
          when === undefined
            ? t("sales.notification.heldUntilUnknown")
            : t("sales.notification.heldUntil", { when }),
      };
    }
    case "queued": {
      // A queued row with a FUTURE `send_after` is waiting out a retry after a
      // failed attempt (ruling 6). Past it, the dispatcher takes the row on
      // its next pass, and "Sending…" is the whole truth.
      if (row.send_after === null || Date.parse(row.send_after) <= nowMs) return undefined;
      const when = formatTenantInstant(row.send_after, timeZone, nowMs, locale);
      return {
        kind: "retry",
        text:
          when === undefined
            ? t("sales.notification.retryScheduled")
            : t("sales.notification.retryAt", { when }),
      };
    }
    case "skipped_superseded":
      // One sentence for all three sources (the header): a newer push went
      // at the release, the order moved on first, or the quote overtook the
      // acknowledgment. The row cannot tell them apart, so neither can this.
      return { kind: "superseded", text: t("sales.notification.superseded") };
    case "skipped_no_channel":
      // The chip says nothing went out; this says what to do about it. A
      // salesperson reading "no channel" learned a word, not a next step.
      return { kind: "no_channel", text: t("sales.notification.noChannel") };
    case "failed": {
      const reason = row.failed_reason ?? "";
      return { kind: "failed", text: t(notificationReasonKey(row.failed_reason)), reason };
    }
    default:
      return undefined;
  }
}

/**
 * The §2.20 rows as a register table. The caller owns the card, the loading
 * and error states and the empty sentence — the order's panel reads rows off
 * the order detail, the quote's card off the log route — and passes its own
 * test ids, so each surface keeps the handles its walkthroughs use.
 */
export function CustomerNotificationTable({
  rows,
  tableTestId,
  rowTestId,
  caption,
  empty,
  pager,
  clientPager,
}: {
  readonly rows: readonly CustomerNotification[];
  readonly tableTestId: string;
  readonly rowTestId: string;
  readonly caption: string;
  readonly empty: ReactNode;
  /** One server page at a time (the composer's log, D-560). */
  readonly pager?: DataTablePager | undefined;
  /** A log the screen holds whole pages at 50 (the order's, D-560). */
  readonly clientPager?: DataTableClientPager | undefined;
}) {
  const { t, i18n } = useTranslation();
  const timeZone = useTenantTimezone();
  const nowMs = Date.now();
  const columns: DataTableColumn<CustomerNotification>[] = [
    {
      key: "kind",
      header: t("sales.notification.colKind"),
      id: true,
      render: (notification) => (
        <span data-testid="sales-notif-kind" data-kind={notification.kind}>
          {t(`sales.notification.kind.${notification.kind}`)}
        </span>
      ),
    },
    {
      key: "channel",
      header: t("sales.notification.colChannel"),
      render: (notification) => t(`sales.channel.${notification.channel}`),
    },
    {
      key: "status",
      header: t("sales.notification.colStatus"),
      render: (notification) => {
        const detail = notificationDetail(t, notification, timeZone, nowMs, i18n.language);
        return (
          <>
            <StatusChip
              variant={notificationChipVariant(notification.status)}
              data-testid="sales-notif-status"
              data-status={notification.status}
            >
              {t(`sales.notification.state.${notification.status}`)}
            </StatusChip>
            {detail !== undefined ? (
              <div
                className="sales-hint"
                data-testid="sales-notif-detail"
                data-detail={detail.kind}
                {...(detail.kind === "failed" ? { "data-reason": detail.reason } : {})}
                {...(detail.kind === "held" || detail.kind === "retry"
                  ? { "data-send-after": notification.send_after ?? "" }
                  : {})}
              >
                {detail.text}
              </div>
            ) : null}
          </>
        );
      },
    },
    {
      key: "created_at",
      header: t("sales.notification.colWhen"),
      age: true,
      render: (notification) => formatTimestamp(notification.created_at, i18n.language, timeZone),
    },
  ];
  return (
    <DataTable
      data-testid={tableTestId}
      columns={columns}
      rows={rows}
      rowKey={(notification) => notification.id}
      empty={empty}
      caption={caption}
      rowTestId={rowTestId}
      rowProps={(notification) => ({
        "data-notification-id": notification.id,
        "data-kind": notification.kind,
        "data-status": notification.status,
      })}
      pager={pager}
      clientPager={clientPager}
    />
  );
}
